import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/FormPages.css";
import DocumentUpload from "../../components/DocumentUpload";
import LocationSelectors from "../../components/LocationSelectors";

const AgreementFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { country, state: stateVal, district, lawCategory } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    country: country || "",
    state: stateVal || "",
    district: district || "",

    party1Name: "",
    party1FatherOrHusband: "",
    party1Age: "",
    party1Gender: "",
    party1Occupation: "",
    party1Contact: "",
    party1Address: "",
    party2Name: "",
    party2FatherOrHusband: "",
    party2Age: "",
    party2Gender: "",
    party2Occupation: "",
    party2Contact: "",
    party2Address: "",
    agreementType: "",
    agreementTypeOther: "",
    agreementTitle: "",
    agreementPurpose: "",
    agreementDetails: "",
    terms: "",
    conditions: "",
    duration: "",
    startDate: "",
    endDate: "",
    paymentTerms: "",
    agreementValue: "",
    obligations: "",
    penaltyClause: "",
    terminationClause: "",
    jurisdiction: "",
    witnessDetails: "",
    specialProvisions: "",
    declaration: false,
  });

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [extractedContent, setExtractedContent] = useState({});

  const handleDocumentsChange = (files, content) => {
    setUploadedDocuments(files);
    setExtractedContent(content);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedCountry = formData.country || country || "";
    const selectedState = formData.state || stateVal || "";
    const selectedDistrict = formData.district || district || "";
    
  let detailsText = `
👤 Party 1 Details:
Name: ${formData.party1Name}
Father's/Husband's Name: ${formData.party1FatherOrHusband}
Age: ${formData.party1Age}
Gender: ${formData.party1Gender}
Occupation: ${formData.party1Occupation}
Contact: ${formData.party1Contact}
Address: ${formData.party1Address}

👥 Party 2 Details:
Name: ${formData.party2Name}
Father's/Husband's Name: ${formData.party2FatherOrHusband}
Age: ${formData.party2Age}
Gender: ${formData.party2Gender}
Occupation: ${formData.party2Occupation}
Contact: ${formData.party2Contact}
Address: ${formData.party2Address}

📋 Agreement Details:
Type: ${formData.agreementTypeOther || formData.agreementType}
Title: ${formData.agreementTitle}
Purpose: ${formData.agreementPurpose}
Details: ${formData.agreementDetails}

⚖️ Terms & Conditions:
Terms: ${formData.terms}
Conditions: ${formData.conditions}
Duration: ${formData.duration}
Start Date: ${formData.startDate}
End Date: ${formData.endDate}
Agreement Value: ${formData.agreementValue}
Payment Terms: ${formData.paymentTerms}

📜 Legal Provisions:
Obligations: ${formData.obligations}
Penalty Clause: ${formData.penaltyClause}
Termination Clause: ${formData.terminationClause}
Jurisdiction: ${formData.jurisdiction}
Witness Details: ${formData.witnessDetails}
Special Provisions: ${formData.specialProvisions}
`.trim();

    if (uploadedDocuments.length > 0) {
      detailsText += `\n\n📄 Supporting Documents:`;
      uploadedDocuments.forEach((doc, idx) => {
        detailsText += `\n\nDocument ${idx + 1}: ${doc.name}`;
        if (extractedContent[doc.id]) {
          detailsText += `\nExtracted Content:\n${extractedContent[doc.id]}`;
        }
      });
    }

    navigate("/drafts", {
      state: {
        query: detailsText,
        summary: detailsText,
        country: selectedCountry,
        state: selectedState,
        district: selectedDistrict,
        lawCategory,
  draftType: "agreement",
  entityName: `${formData.party1Name} & ${formData.party2Name}`,
  uploadedDocuments,
  extractedContent,
      },
    });
  };

  const renderStep1 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👤 Party 1 Details (First Party)
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="party1Name"
            value={formData.party1Name}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="party1FatherOrHusband"
            value={formData.party1FatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="party1Age"
            value={formData.party1Age}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="party1Gender"
            value={formData.party1Gender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation *</label>
          <input
            type="text"
            name="party1Occupation"
            value={formData.party1Occupation}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="party1Contact"
            value={formData.party1Contact}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Address *</label>
          <textarea
            name="party1Address"
            value={formData.party1Address}
            onChange={handleInputChange}
            rows={3}
            required
          />
        </div>
      </div>

      <LocationSelectors
        country={formData.country}
        state={formData.state}
        district={formData.district}
        onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👥 Party 2 Details (Second Party)
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="party2Name"
            value={formData.party2Name}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="party2FatherOrHusband"
            value={formData.party2FatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="party2Age"
            value={formData.party2Age}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="party2Gender"
            value={formData.party2Gender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation *</label>
          <input
            type="text"
            name="party2Occupation"
            value={formData.party2Occupation}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="party2Contact"
            value={formData.party2Contact}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Address *</label>
          <textarea
            name="party2Address"
            value={formData.party2Address}
            onChange={handleInputChange}
            rows={3}
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        📋 Agreement Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Agreement Type *</label>
          <select
            name="agreementType"
            value={formData.agreementType}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Agreement Type</option>
            <option value="Service Agreement">Service Agreement</option>
            <option value="Employment Contract">Employment Contract</option>
            <option value="Partnership Agreement">Partnership Agreement</option>
            <option value="Non-Disclosure Agreement">Non-Disclosure Agreement</option>
            <option value="Rental Agreement">Rental Agreement</option>
            <option value="Sales Agreement">Sales Agreement</option>
            <option value="Licensing Agreement">Licensing Agreement</option>
            <option value="Joint Venture Agreement">Joint Venture Agreement</option>
            <option value="Consultancy Agreement">Consultancy Agreement</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
        <div className="form-page-col">
          {formData.agreementType === "Other" && (
            <>
              <label>Specify Agreement Type *</label>
              <input
                type="text"
                name="agreementTypeOther"
                value={formData.agreementTypeOther}
                onChange={handleInputChange}
                required
              />
            </>
          )}
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Agreement Title *</label>
          <input
            type="text"
            name="agreementTitle"
            value={formData.agreementTitle}
            onChange={handleInputChange}
            placeholder="Title or heading for this agreement"
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Agreement Purpose *</label>
          <textarea
            name="agreementPurpose"
            value={formData.agreementPurpose}
            onChange={handleInputChange}
            rows={3}
            placeholder="Purpose and objective of this agreement..."
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Agreement Details *</label>
          <textarea
            name="agreementDetails"
            value={formData.agreementDetails}
            onChange={handleInputChange}
            rows={4}
            placeholder="Detailed description of the agreement scope and deliverables..."
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <>
      <div className="form-page-section">
        <div className="form-page-section-title">
          ⚖️ Terms, Conditions & Legal Provisions
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Terms & Conditions *</label>
            <textarea
              name="terms"
              value={formData.terms}
              onChange={handleInputChange}
              rows={3}
              placeholder="Main terms of the agreement..."
              required
            />
          </div>
          <div className="form-page-col">
            <label>Additional Conditions</label>
            <textarea
              name="conditions"
              value={formData.conditions}
              onChange={handleInputChange}
              rows={3}
              placeholder="Special conditions and clauses..."
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Duration/Validity</label>
            <input
              type="text"
              name="duration"
              value={formData.duration}
              onChange={handleInputChange}
              placeholder="e.g., 1 year, 6 months, etc."
            />
          </div>
          <div className="form-page-col">
            <label>Start Date</label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>End Date</label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-page-col">
            <label>Agreement Value (₹)</label>
            <input
              type="number"
              name="agreementValue"
              value={formData.agreementValue}
              onChange={handleInputChange}
              placeholder="Total value of agreement"
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Payment Terms</label>
            <textarea
              name="paymentTerms"
              value={formData.paymentTerms}
              onChange={handleInputChange}
              rows={2}
              placeholder="Payment schedule, milestones, and terms..."
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Obligations & Responsibilities</label>
            <textarea
              name="obligations"
              value={formData.obligations}
              onChange={handleInputChange}
              rows={3}
              placeholder="Obligations of each party..."
            />
          </div>
          <div className="form-page-col">
            <label>Penalty Clause</label>
            <textarea
              name="penaltyClause"
              value={formData.penaltyClause}
              onChange={handleInputChange}
              rows={3}
              placeholder="Penalties for breach of agreement..."
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Termination Clause</label>
            <textarea
              name="terminationClause"
              value={formData.terminationClause}
              onChange={handleInputChange}
              rows={2}
              placeholder="Conditions for termination..."
            />
          </div>
          <div className="form-page-col">
            <label>Jurisdiction</label>
            <input
              type="text"
              name="jurisdiction"
              value={formData.jurisdiction}
              onChange={handleInputChange}
              placeholder="Court jurisdiction for disputes"
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Witness Details</label>
            <textarea
              name="witnessDetails"
              value={formData.witnessDetails}
              onChange={handleInputChange}
              rows={2}
              placeholder="Witness names and contact details..."
            />
          </div>
          <div className="form-page-col">
            <label>Special Provisions</label>
            <textarea
              name="specialProvisions"
              value={formData.specialProvisions}
              onChange={handleInputChange}
              rows={2}
              placeholder="Any special clauses or provisions..."
            />
          </div>
        </div>
      </div>
      <div className="form-page-section">
        <div className="form-page-section-title">📄 Supporting Documents</div>
        <p className="form-page-note">
          Upload related documents (draft versions, prior agreements, correspondence, invoices, proofs). Text will be extracted to enrich the generated agreement.
        </p>
        <DocumentUpload
          onDocumentsChange={handleDocumentsChange}
          maxFiles={8}
          acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
        />
      </div>

      <div className="form-page-declaration">
        <div className="form-page-section-title">
          ✅ Declaration
        </div>
        <label>
          <input
            type="checkbox"
            name="declaration"
            checked={formData.declaration}
            onChange={handleInputChange}
            required
          />
          <span>Both parties agree to the terms and conditions mentioned in this agreement *</span>
        </label>
      </div>
    </>
  );

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <a href="#" onClick={() => navigate(-1)} className="form-page-back-link">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Draft Selection
        </a>
        <h1 className="form-page-title">Create Agreement Document</h1>
        <p className="form-page-subtitle">Fill in the details below to generate a professional agreement</p>
      </div>

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          <div className="form-page-navigation">
            <div className="form-page-step-indicator">
              Step {currentStep} of {totalSteps}
            </div>
            <div className="form-page-buttons">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="form-page-btn form-page-btn-secondary"
                >
                  Previous
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="form-page-btn form-page-btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="form-page-btn form-page-btn-primary"
                >
                  Generate Agreement Document
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgreementFormPage;
